import React, { useState, useEffect } from "react";
import PropTypes from "prop-types";
import getSymbolFromCurrency from "currency-symbol-map";
import ReactSelect, { components } from "react-select";
import currencyCodes from "currency-codes";
import { toast } from "react-toastify";
import { SERVER_BASE_URL } from "../config";
import { useAuth } from "../context/AuthContext";


const SingleValue = (props) => {
  return (
    <components.SingleValue {...props}>
      {props.data.value}
    </components.SingleValue>
  );
};

SingleValue.propTypes = {
  data: PropTypes.shape({
    value: PropTypes.string.isRequired,
  }).isRequired,
};

const currencyOptions = currencyCodes.codes().map((code) => ({
  value: code,
  label: `${code} - ${currencyCodes.code(code).currency}`,
}));

function EventForm({
  title,
  // Задаём дефолтные значения для initialData прямо в параметрах функции:
  initialData = {
    name: "",
    description: "",
    place: "",
    start_time: null,
    state: "confirmed",
    admission: "",
    currency: "USD",
    event_duration: 20,
  },
  onSubmit,
  onCancel,
}) {
  const { user } = useAuth(); // Получаем пользователя из контекста авторизации
  
  const [availableWorkers, setAvailableWorkers] = useState([]);
  const [dateError, setDateError] = useState(false);
  // Добавляем новое состояние для отслеживания открытия календаря
  const [calendarOpen, setCalendarOpen] = useState(false);

  const [formData, setFormData] = useState(() => {
    let initialWorker = initialData?.worker || "";
    if (initialWorker && typeof initialWorker !== "string") {
      initialWorker = String(initialWorker);
    }
    return {
      name: initialData?.name || "",
      description: initialData?.description || "",
      place: initialData?.place || "",
      start_time: initialData?.start_time || "",
      state: initialData?.state || "confirmed",
      admission: initialData?.admission || localStorage.getItem("defaultAdmission") || "",
      currency: initialData?.currency || "USD",
      event_duration: initialData?.event_duration || 
               user?.default_event_duration || 
               localStorage.getItem("defaultEventDuration") || 
               24,
      worker: initialWorker,
    };
  });

  // При первом рендере проверяем, не прошла ли дата события
  useEffect(() => {
    if (formData.start_time) {
      const eventDate = new Date(formData.start_time);
      const now = new Date();
      if (eventDate < now) {
        setDateError(true);
      }
    }
  }, [formData.start_time]);

  // Загружаем список доступных воркеров (для поля Entrance)
  useEffect(() => {
    const fetchWorkers = async () => {
      try {
        const token = localStorage.getItem("token");
        const response = await fetch(`${SERVER_BASE_URL}/api/users/my-workers`, {
          headers: {
            Authorization: `Bearer ${token}`,
          },
        });
        if (response.ok) {
          const data = await response.json();
          const workersArray = data.workers ? data.workers : data;
          setAvailableWorkers(Array.isArray(workersArray) ? workersArray : []);
          // Если worker не указан, но есть дефолтный в ответе (DOOR)
          if (!formData.worker && data.default_workers && data.default_workers.DOOR) {
            setFormData((prev) => ({
              ...prev,
              worker: data.default_workers.DOOR.toString(),
            }));
          }
        } else {
          console.error("Failed to fetch workers");
        }
      } catch (error) {
        console.error("Error fetching workers:", error);
      }
    };
    fetchWorkers();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  // Отслеживаем клики на документе для определения, когда календарь закрывается
  useEffect(() => {
    const handleClickOutside = (e) => {
      // Проверяем, был ли клик вне полей даты и времени
      const dateInput = document.querySelector('input[type="date"]');
      const timeInput = document.querySelector('input[type="time"]');
      
      // Если клик был по элементам формы, кроме полей даты/времени, закрываем затемнение
      if (calendarOpen && 
          e.target !== dateInput && 
          e.target !== timeInput && 
          !e.target.closest('.calendar-popup')) {
        setCalendarOpen(false);
      }
    };
    
    // Проверяем потерю фокуса с полей даты и времени
    const handleBlur = () => {
      if (calendarOpen) {
        // Даем время для обработки клика, если он был по элементу календаря
        setTimeout(() => {
          const activeElement = document.activeElement;
          const dateInput = document.querySelector('input[type="date"]');
          const timeInput = document.querySelector('input[type="time"]');
          
          // Если фокус не на полях даты/времени и не на элементе календаря
          if (activeElement !== dateInput && activeElement !== timeInput) {
            setCalendarOpen(false);
          }
        }, 200);
      }
    };
    
    document.addEventListener('mousedown', handleClickOutside);
    document.addEventListener('blur', handleBlur, true);
    
    return () => {
      document.removeEventListener('mousedown', handleClickOutside);
      document.removeEventListener('blur', handleBlur, true);
    };
  }, [calendarOpen]);

  const handleChange = (e) => {
    const { name, value } = e.target;
    
    // Если меняется продолжительность - проверяем на валидность
    if (name === "event_duration") {
      // Разрешаем ввод только числовых значений
      const numericValue = value.replace(/[^0-9.]/g, '');
      setFormData((prev) => ({ ...prev, [name]: numericValue }));
    } else {
      setFormData((prev) => ({ ...prev, [name]: value }));
    }
  };

  const handleCurrencyChange = (selectedOption) => {
    setFormData((prev) => ({ ...prev, currency: selectedOption.value }));
  };

  const handleStartDateChange = (e) => {
    const date = e.target.value;
    const time = formData.start_time?.split("T")[1] || "20:00";
    const newDateTime = `${date}T${time}`;
    setFormData((prev) => ({ ...prev, start_time: newDateTime }));
  
    // Проверяем, не прошла ли дата
    const eventDate = new Date(newDateTime);
    const now = new Date();
    setDateError(eventDate < now);
    
    // Сигнализируем о том, что дата была изменена - календарь должен закрыться
    handleDateChanged();
  };

  const handleStartTimeChange = (e) => {
    const time = e.target.value;
    const date = formData.start_time?.split("T")[0] || new Date().toISOString().split("T")[0];
    const newDateTime = `${date}T${time}`;
    setFormData((prev) => ({ ...prev, start_time: newDateTime }));
  
    // Проверяем, не прошла ли дата
    const eventDate = new Date(newDateTime);
    const now = new Date();
    setDateError(eventDate < now);
    
    // Убеждаемся, что затемнение выключено при ручном вводе времени
    setCalendarOpen(false);
  };

  const handleSubmit = (e) => {
    e.preventDefault();
    if (dateError) {
      alert("Cannot create an event in the past. Please select a future date.");
      return;
    }
    
    // Преобразуем в число и проверяем на корректность
    let durationValue = parseFloat(formData.event_duration);
    if (isNaN(durationValue) || durationValue <= 0) {
      durationValue = 24; // Используем дефолтное значение при ошибке
    }
    
    const payload = { 
      ...formData,
      // Всегда используем числовое значение
      event_duration: durationValue
    };
    
    // Проверка и установка start_time
    if (!payload.start_time) {
      // Если start_time не задан, устанавливаем значение по умолчанию (завтра, 20:00)
      const tomorrow = new Date();
      tomorrow.setDate(tomorrow.getDate() + 1);
      tomorrow.setHours(20, 0, 0, 0);
      payload.start_time = tomorrow.toISOString();
      
      // Показываем уведомление пользователю
      toast.info("Дата начала не была указана. Установлено значение по умолчанию (завтра, 20:00).");
    } if (typeof payload.start_time === 'string' && !payload.start_time.endsWith('Z') && !payload.start_time.includes('+')) {
      const utcDate = new Date(payload.start_time);
      payload.start_time = utcDate.toISOString(); // ← эта строка теперь будет offset-aware: ISO + Z
    }
    // Расчет end_time на основе start_time и duration
    if (payload.start_time) {
      const startTime = new Date(payload.start_time);
      const endTime = new Date(startTime.getTime() + durationValue * 60 * 60 * 1000);
      payload.end_time = endTime.toISOString();
    }
    
    if (payload.worker) {
      payload.entrance_worker_id = Number(payload.worker);
      delete payload.worker;
    }
    
    onSubmit(payload);
  };

  // Обработчики открытия/закрытия календаря
  const handleCalendarClick = (e) => {
    // Определяем, был ли клик по иконке календаря, а не по текстовому полю
    // Это можно определить по координатам клика - иконка календаря обычно справа
    const rect = e.target.getBoundingClientRect();
    const clickX = e.clientX;
    
    // Если клик был в правой части поля (где находится иконка календаря)
    // Обычно иконка календаря занимает примерно 20% ширины поля справа
    const calendarIconThreshold = rect.right - (rect.width * 0.2);
    
    if (clickX > calendarIconThreshold) {
      // Клик был по иконке календаря, активируем затемнение
      setCalendarOpen(true);
    } else {
      // Клик был в текстовой части поля, не активируем затемнение
      setCalendarOpen(false);
    }
  };
  
  // Обработчик изменения даты - календарь закрылся
  const handleDateChanged = () => {
    // Даем небольшую задержку для закрытия календаря
    setTimeout(() => {
      setCalendarOpen(false);
    }, 50);
  };

  const dateValue = formData.start_time ? formData.start_time.substring(0, 10) : "";
  const timeValue = (() => {
  if (!formData.start_time) return "20:00";
  const local = new Date(formData.start_time);
  return local.toTimeString().slice(0, 5); // ← локальное время в HH:MM
})();
  const today = new Date().toISOString().split("T")[0];

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center p-4 bg-black bg-opacity-50 overflow-y-auto">
      {/* Дополнительный затемняющий слой когда открыт календарь */}
      {calendarOpen && (
        <div className="fixed inset-0 bg-black bg-opacity-70 z-50" style={{ pointerEvents: "none" }}></div>
      )}
      
      <div className="bg-white rounded-lg shadow-lg w-full max-w-md max-h-[90vh] overflow-y-auto relative z-60">
        {/* Шапка модалки */}
        <div className="sticky top-0 bg-white p-2 border-b z-10 flex justify-between items-center">
          <h2 className="text-xl font-bold text-gray-800">{title}</h2>
          <button
            onClick={onCancel}
            className="text-gray-500 hover:text-gray-700 transition duration-200"
            aria-label="Close"
          >
            <svg
              xmlns="http://www.w3.org/2000/svg"
              className="h-6 w-6"
              fill="none"
              viewBox="0 0 24 24"
              stroke="currentColor"
            >
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
            </svg>
          </button>
        </div>

        <div className="p-3">
          <form onSubmit={handleSubmit} className="space-y-3">
            {/* Event Name */}
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">Event Name:</label>
              <input
                type="text"
                name="name"
                placeholder="Event Name"
                value={formData.name}
                onChange={handleChange}
                required
                className="w-full border border-gray-300 p-2 rounded-md focus:ring-indigo-500 focus:border-indigo-500"
              />
            </div>

            {/* Place */}
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">Place (Location):</label>
              <input
                type="text"
                name="place"
                value={formData.place}
                onChange={handleChange}
                className="w-full border border-gray-300 p-2 rounded-md focus:ring-indigo-500 focus:border-indigo-500"
                placeholder="Enter place or location"
              />
            </div>

            {/* Date, Time, Duration */}
            <div className="flex space-x-2">
                <div className="w-1/3 relative">
                <label className="block text-sm font-medium text-gray-700 mb-1">Start Date:</label>
                <div className="relative">
                  <input
                    type="date"
                    name="date"
                    value={dateValue}
                    onChange={handleStartDateChange}
                    onClick={handleCalendarClick}
                    required
                    className={`w-full border ${
                      dateError ? "border-red-500" : "border-gray-300"
                    } p-2 rounded-md focus:ring-indigo-500 focus:border-indigo-500 relative z-70`}
                    min={today}
                  />
                </div>
              </div>
              <div className="w-1/3">
                <label className="block text-sm font-medium text-gray-700 mb-1">Start Time:</label>
                <input
                  type="time"
                  name="time"
                  value={timeValue}
                  onChange={handleStartTimeChange}
                  onClick={handleCalendarClick}
                  required
                  className="w-full border border-gray-300 p-2 rounded-md focus:ring-indigo-500 focus:border-indigo-500 relative z-70"
                />
              </div>
              <div className="w-1/3">
                <label className="block text-sm font-medium text-gray-700 mb-1">Duration (h):</label>
                <input
                  type="number"
                  name="event_duration"
                  value={formData.event_duration || ""}
                  onChange={handleChange}
                  className="w-full border border-gray-300 p-2 rounded-md focus:ring-indigo-500 focus:border-indigo-500"
                />
              </div>
            </div>

            {/* Entrance, Admission, Currency */}
            <div className="flex space-x-2">
              <div className="w-1/3">
                <label className="block text-sm font-medium text-gray-700 mb-1">Entrance:</label>
                <select
                  name="worker"
                  value={formData.worker}
                  onChange={handleChange}
                  className="w-full border border-gray-300 p-2 rounded-md focus:ring-indigo-500 focus:border-indigo-500"
                >
                  <option value="">—</option>
                  {availableWorkers.map((worker) => (
                    <option key={worker.id} value={String(worker.id)}>
                      {worker.name || worker.email}
                    </option>
                  ))}
                </select>
              </div>
              <div className="w-1/3">
                <label className="block text-sm font-medium text-gray-700 mb-1">
                  Admission ({getSymbolFromCurrency(formData.currency) || ""}):
                </label>
                <input
                  type="number"
                  name="admission"
                  value={formData.admission}
                  onChange={handleChange}
                  className="w-full border border-gray-300 p-2 rounded-md focus:ring-indigo-500 focus:border-indigo-500"
                />
              </div>
              <div className="w-1/3">
                <label className="block text-sm font-medium text-gray-700 mb-1">Currency:</label>
                <ReactSelect
                  name="currency"
                  value={currencyOptions.find(
                    (option) => option.value === formData.currency
                  )}
                  options={currencyOptions}
                  onChange={handleCurrencyChange}
                  components={{ SingleValue }}
                  className="react-select-container"
                  classNamePrefix="react-select"
                  styles={{
                    control: (provided) => ({
                      ...provided,
                      minHeight: "38px",
                      height: "38px",
                      borderColor: "#E5E7EB",
                    }),
                    valueContainer: (provided) => ({
                      ...provided,
                      height: "38px",
                      padding: "0 8px",
                    }),
                    indicatorsContainer: (provided) => ({
                      ...provided,
                      height: "38px",
                    }),
                  }}
                />
              </div>
            </div>

            {/* State */}
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">State:</label>
              <div className="flex space-x-4">
                <label className="flex items-center cursor-pointer text-gray-700">
                  <input
                    type="radio"
                    name="state"
                    value="confirmed"
                    checked={formData.state === "confirmed"}
                    onChange={handleChange}
                    className="mr-1"
                    style={{
                      accentColor: formData.state === "confirmed" ? "green" : undefined,
                    }}
                  />
                  Confirmed
                </label>
                <label className="flex items-center cursor-pointer text-gray-700">
                  <input
                    type="radio"
                    name="state"
                    value="planned"
                    checked={formData.state === "planned"}
                    onChange={handleChange}
                    className="mr-1"
                    style={{
                      accentColor: formData.state === "planned" ? "blue" : undefined,
                    }}
                  />
                  Planned
                </label>
                <label className="flex items-center cursor-pointer text-gray-700">
                  <input
                    type="radio"
                    name="state"
                    value="cancelled"
                    checked={formData.state === "cancelled"}
                    onChange={handleChange}
                    className="mr-1"
                    style={{
                      accentColor: formData.state === "cancelled" ? "red" : undefined,
                    }}
                  />
                  Cancelled
                </label>
              </div>
            </div>

            {/* Description */}
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">Description:</label>
              <textarea
                name="description"
                placeholder="Description"
                value={formData.description}
                onChange={handleChange}
                className="w-full border border-gray-300 p-2 rounded-md focus:ring-indigo-500 focus:border-indigo-500"
                style={{ height: "80px" }}
              />
            </div>

            {/* Buttons */}
            <div className="sticky bottom-0 bg-white pt-3 border-t mt-2 flex justify-center space-x-3">
              <button
                type="submit"
                className={`px-5 py-2 font-medium rounded transition duration-200 ${
                  dateError
                    ? "bg-gray-400 text-white cursor-not-allowed"
                    : "bg-indigo-600 text-white hover:bg-indigo-700"
                }`}
                disabled={dateError}
              >
                {title === "Copy Event" ? "Copy Event" : title}
              </button>
              <button
                type="button"
                onClick={onCancel}
                className="px-5 py-2 bg-white text-gray-700 font-medium rounded hover:bg-gray-100 border border-gray-300 transition duration-200"
              >
                Cancel
              </button>
            </div>
          </form>
        </div>
      </div>
    </div>
  );
}

EventForm.propTypes = {
  title: PropTypes.string.isRequired,
  initialData: PropTypes.object,
  onSubmit: PropTypes.func.isRequired,
  onCancel: PropTypes.func.isRequired,
};

export default EventForm;